/**
 * Destek talebine iliştirilen teknik ortam bilgisi.
 *
 * Amaç, destek ekibinin "hangi tarayıcıyı kullanıyorsunuz" turunu ortadan
 * kaldırmak. Yalnızca ortam verisi toplanır; kişisel içerik toplanmaz.
 */

export interface DiagnosticsInfo {
  browser: string;
  os: string;
  screen: string;
  viewport: string;
  timezone: string;
  language: string;
  /** Sorunun yaşandığı sayfa — destek sayfalarında anlamsız olduğu için boş bırakılır */
  page?: string;
  build?: string;
}

interface UserAgentDataLike {
  brands?: { brand: string; version: string }[];
  platform?: string;
}

const UA_BROWSERS: { name: string; pattern: RegExp }[] = [
  { name: "Edge", pattern: /Edg\/([\d.]+)/ },
  { name: "Opera", pattern: /OPR\/([\d.]+)/ },
  { name: "Chrome", pattern: /Chrome\/([\d.]+)/ },
  { name: "Firefox", pattern: /Firefox\/([\d.]+)/ },
  { name: "Safari", pattern: /Version\/([\d.]+).*Safari/ },
];

const UA_PLATFORMS: { name: string; pattern: RegExp }[] = [
  { name: "Android", pattern: /Android\s([\d.]+)/ },
  { name: "iOS", pattern: /(?:iPhone|iPad).*OS\s([\d_]+)/ },
  { name: "macOS", pattern: /Mac OS X\s([\d_]+)/ },
  { name: "Windows", pattern: /Windows NT\s([\d.]+)/ },
];

const detectBrowser = (userAgent: string): string => {
  const uaData = (navigator as Navigator & { userAgentData?: UserAgentDataLike })
    .userAgentData;

  // Chromium tabanlı tarayıcılar UA-CH ile daha güvenilir bilgi verir
  const brand = uaData?.brands?.find(
    (item) => !/Not.?A.?Brand|Chromium/i.test(item.brand),
  );
  if (brand) return `${brand.brand} ${brand.version}`;

  for (const { name, pattern } of UA_BROWSERS) {
    const match = userAgent.match(pattern);
    if (match) return `${name} ${match[1].split(".")[0]}`;
  }
  return "Unknown";
};

const detectOS = (userAgent: string): string => {
  for (const { name, pattern } of UA_PLATFORMS) {
    const match = userAgent.match(pattern);
    if (match) return `${name} ${match[1].replace(/_/g, ".")}`;
  }
  return navigator.platform || "Unknown";
};

/** Destek rotalarındaki URL tanı için bilgi taşımaz, atlanır */
const isSupportRoute = (pathname: string) => pathname.startsWith("/support");

export const collectDiagnostics = (): DiagnosticsInfo => {
  const userAgent = navigator.userAgent;
  const ratio = window.devicePixelRatio;

  return {
    browser: detectBrowser(userAgent),
    os: detectOS(userAgent),
    screen: `${window.screen.width}×${window.screen.height}${
      ratio && ratio !== 1 ? ` @${ratio}x` : ""
    }`,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    page: isSupportRoute(window.location.pathname)
      ? undefined
      : window.location.pathname + window.location.search,
    build: import.meta.env.MODE,
  };
};
